import type { ReactNode } from 'react';
import { Card, Divider, Heading, Text, VStack } from '@astryxdesign/core';

const LAST_UPDATED = 'August 17, 2026';

interface Item {
  title: string;
  body: ReactNode;
}

const SECTIONS: Item[] = [
  {
    title: '1. What this covers',
    body: (
      <>
        This policy explains what data VecTime Cloud ("VecTime", "we", "us") collects when you use
        our website, dashboard, and forecasting API (together, the "Service"), why we collect it,
        and the choices you have. It applies to visitors, registered account holders, and anyone
        whose API keys call the Service on their behalf.
      </>
    ),
  },
  {
    title: '2. Data you give us',
    body: (
      <>
        <Text size="sm" color="secondary">Account data: name, email address, and password (stored
        only as a salted hash, never in plain text) when you register.</Text>
        <Text size="sm" color="secondary">Billing data: when you subscribe to a paid plan, our
        payment processor, Stripe, collects your card details directly. We never see or store full
        card numbers — we retain only the subscription status, plan, and invoice history Stripe
        provides back to us.</Text>
        <Text size="sm" color="secondary">Support data: anything you send us in an email or support
        request.</Text>
      </>
    ),
  },
  {
    title: '3. Data we collect automatically',
    body: (
      <>
        <Text size="sm" color="secondary">API usage: each call to <span className="mono">POST
        /v1/forecast</span> is logged with timestamp, status code, latency, and horizon, and rolled
        up into daily usage counts so we can enforce plan limits and show you the numbers in the
        dashboard. We do not log the numeric contents of the time series you submit for
        forecasting.</Text>
        <Text size="sm" color="secondary">API keys: we store an HMAC of each key, not the plaintext.
        The plaintext is shown to you exactly once, at creation.</Text>
        <Text size="sm" color="secondary">Session data: a signed session token (JWT) is set as a
        cookie or bearer token to keep you signed in to the dashboard.</Text>
        <Text size="sm" color="secondary">Standard web logs: IP address, browser user agent, and
        request timestamps for the website and API, kept for security and abuse investigation.</Text>
      </>
    ),
  },
  {
    title: '4. How we use this data',
    body: (
      <>
        To provide and meter the Service (authenticate requests, enforce rate limits and monthly
        quotas, run forecasts, show usage in the dashboard); to bill you (via Stripe) for paid
        plans; to send account and billing emails — verification, receipts, plan changes, and
        service notices; to detect abuse, fraud, and outages; and to improve reliability and
        performance of the API.
      </>
    ),
  },
  {
    title: '5. Who we share it with',
    body: (
      <>
        We do not sell your data. We share it only with the processors that make the Service work:
        Stripe (payments and billing), our email delivery provider (account and transactional
        email), and our cloud infrastructure and database hosting providers (running the API and
        storing your account, key, and usage records). Each is bound to use your data only to
        provide their service to us. We may also disclose data if required by law or to protect the
        rights, safety, or property of VecTime or our users.
      </>
    ),
  },
  {
    title: '6. Time series content',
    body: (
      <>
        The data you POST to <span className="mono">/v1/forecast</span> for forecasting is processed
        in memory to produce your response and is not retained after the request completes, beyond
        transient logs kept briefly for debugging and abuse prevention.
      </>
    ),
  },
  {
    title: '7. Data retention',
    body: (
      <>
        We keep account data for as long as your account is active. Usage logs and aggregated usage
        counts are kept for as long as needed for billing history, quota resets, and abuse
        investigation. If you delete your account, we delete or anonymize personal data within a
        reasonable period, except where we must retain records for legal, tax, or billing-dispute
        purposes.
      </>
    ),
  },
  {
    title: '8. Your choices',
    body: (
      <>
        You can review and update your account details from the dashboard, rotate or revoke API
        keys at any time, and request a copy or deletion of your personal data by contacting us at
        the address below. If you are in a jurisdiction with statutory data rights (such as the
        EU/UK GDPR or the California CCPA/CPRA), those rights apply to you as described by that
        law, and this section does not limit them.
      </>
    ),
  },
  {
    title: '9. Cookies',
    body: (
      <>
        We use only the cookies or local storage needed to keep you signed in and remember basic
        preferences. We do not use third-party advertising trackers.
      </>
    ),
  },
  {
    title: "10. Children's privacy",
    body: (
      <>
        The Service is not directed at children under 16, and we do not knowingly collect data from
        them.
      </>
    ),
  },
  {
    title: '11. Changes to this policy',
    body: (
      <>
        If we make material changes, we will update the date below and, for significant changes,
        notify account holders by email.
      </>
    ),
  },
  {
    title: '12. Contact',
    body: (
      <>
        Questions about this policy or your data: <span className="mono">privacy@vectime.cloud</span>.
      </>
    ),
  },
];

export function PrivacyPage() {
  return (
    <div className="shell section-block">
      <VStack gap={4}>
        <VStack gap={1}>
          <Heading level={1}>Privacy Policy</Heading>
          <Text color="secondary">Last updated {LAST_UPDATED}</Text>
        </VStack>

        <Card padding={3} variant="muted">
          <Text size="sm" color="secondary">
            <span className="measure--wide">
              This is a plain-language summary of our data practices, not a substitute for legal
              advice. If anything here conflicts with our Terms of Service, the Terms govern.
            </span>
          </Text>
        </Card>

        <VStack gap={4}>
          {SECTIONS.map((s, i) => (
            <VStack gap={1} key={s.title}>
              <Heading level={2}>{s.title}</Heading>
              <div className="measure--wide">
                <VStack gap={1}>{s.body}</VStack>
              </div>
              {i < SECTIONS.length - 1 && <Divider variant="subtle" />}
            </VStack>
          ))}
        </VStack>
      </VStack>
    </div>
  );
}
